import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { Messages } from '../../../gen-src/Localize';
import { TextAreaProps } from '../TextAreaWidget';
import { SelectedToggle, ToggleTextWidget } from '../ToggleTextWidget';
import { TextFieldBorderColors } from '../../styles/Colors';
import { makeTestId } from '../../utils/Declarative';
import { generateId } from '../../utils/DomUtil';
import { DivAttrs } from '../../utils/HtmlAttrs';
import { DefaultAllowedTags, TextFormatterDisplayProps, TextFormatterDisplayWidget, TextFormatterMinWidth } from './TextFormatterDisplayWidget';
import { TextFormatterEditorWidget } from './TextFormatterEditorWidget';
import { TextFormatterToolbarHeight } from './TextFormatterToolbar';

export { TextFormatterIcon, TextFormatterNames } from '../../icons/TextFormatterIcons';
export { DefaultAllowedTags, TextFormatterDisplayWidget, TextFormatterDisplayProps };

const TextFormatterTestId = 'markup-editor';

const LabelContainerStyleName = TypeStyle.style({
  alignItems: 'baseline',
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: 24,
});

const HiddenStyleName = TypeStyle.style({
  visibility: 'hidden',
});

const MarkupContainerStyleName = TypeStyle.style({
  minWidth: TextFormatterMinWidth,
  position: 'relative',
});

const PseudoTextAreaStyleName = TypeStyle.style({
  border: `1px solid ${TextFieldBorderColors.Rest}`,
  boxSizing: 'border-box',
  fontSize: 12,
  overflow: 'auto',
  padding: 8,
});

const TextFormatterDisplayStyleName = TypeStyle.style({
  height: `calc(100% - ${TextFormatterToolbarHeight}px)`,
  marginTop: TextFormatterToolbarHeight,
  position: 'absolute',
  top: 0,
  width: '100%',
  $nest: {
    '& > :first-child': {
      marginTop: 0,
    },
  }
});

export interface TextFormatterProps extends Pick<TextAreaProps, 'valid' | 'message'>, Pick<TextFormatterDisplayProps, 'allowedTags'> {
  disabled?: boolean;
  /**
   * The `EditorMode` to initially display
   * @default 'edit'
   */
  initialEditorMode?: EditorMode;
  /** Label to display above the field. Must be provided to be WCAG-compliant. */
  label: React.ReactNode;
  /** Markup to display in the editor */
  markup: string;
  /**
   * Called when the text value of the editor changes.
   * You likely want to pass the value from this back into the `markup` prop.
   */
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
  /** className applied to the div element which holds the Preview's rendered HTML output. */
  previewClassName?: string;
  /** Adds suffix '-TextFormatterWidget' */
  testId?: string;
}

export type EditorMode = 'preview' | 'edit';

const ModeToggleButton: React.FC<{ inPreviewMode: boolean, onClick: () => void }> = ({ inPreviewMode, onClick }) =>
  // TODO change this to an Icon toggle when design is finalized
  <ToggleTextWidget
    ariaLabel={Messages.editorMode()}
    className={TypeStyle.style({ $nest: { '& > label': { flexBasis: 'auto !important' } } })} // can delete this post-IE11
    leftToggleText={Messages.edit()}
    handleLeftToggleSelected={inPreviewMode ? onClick : null!}
    rightToggleText={Messages.preview()}
    handleRightToggleSelected={inPreviewMode ? null! : onClick}
    selectedToggle={inPreviewMode ? SelectedToggle.Right : SelectedToggle.Left}
    testId='EditorModeToggleButton'
  />;

export const TextFormatterWidget: React.FC<TextFormatterProps & Omit<DivAttrs, 'onChange'>> = props => {
  const { allowedTags, disabled, initialEditorMode, label, markup, message, onChange, previewClassName, testId, valid, ...restProps } = props;

  const [inPreviewMode, setInPreviewMode] = React.useState<boolean>(props.initialEditorMode === 'preview');
  const [textAreaId] = React.useState<string>(generateId());

  const handleModeToggleClick = React.useCallback(() => {
    setInPreviewMode(!inPreviewMode);
  }, [setInPreviewMode, inPreviewMode]);

  return <div {...restProps} data-tb-test-id={makeTestId(testId!, '-TextFormatterWidget')}>
    <div className={LabelContainerStyleName}>
      <label htmlFor={textAreaId}>{label}</label>
      <ModeToggleButton inPreviewMode={inPreviewMode} onClick={handleModeToggleClick} />
    </div>
    <div className={MarkupContainerStyleName}>
      <TextFormatterEditorWidget
        className={TypeStyle.classes(inPreviewMode && HiddenStyleName)}
        disabled={disabled || inPreviewMode}
        disabledTooltipText={inPreviewMode ? Messages.textEditorDisabledPreviewMode() : undefined}
        id={textAreaId}
        label={null} // null because we provide our own label above, so it aligns nicely with the mode toggle
        markup={markup}
        message={message || <br />}
        onChange={onChange}
        testId={TextFormatterTestId}
        valid={valid}
      />
      {inPreviewMode && <TextFormatterDisplayWidget
        allowedTags={allowedTags}
        aria-label={Messages.preview()}
        className={TypeStyle.classes(previewClassName, TextFormatterDisplayStyleName, PseudoTextAreaStyleName, !inPreviewMode && HiddenStyleName)}
        markup={markup}
        testId={TextFormatterTestId}
      />}
    </div>
  </div>;
};
TextFormatterWidget.defaultProps = { testId: '' };
TextFormatterWidget.displayName = 'TextFormatterWidget';
